import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import type { ModuleDeclaration } from "didi";
import React, { useEffect, useRef } from "react";
import customModdleExtension from "../flows/moddle/custom.json";
import customWFSharedElementsModule from "../flows/moddle/customWFSharedElementsModule";
import "./workflow-bpmn-diff.scss";

// Minimal slice of the bpmn-js canvas service we use (avoids `any` while staying decoupled).
interface BpmnCanvas {
  zoom(mode: string): void;
  addMarker(elementId: string, marker: string): void;
}

interface WorkflowBpmnPaneProps {
  xml?: string;
  changedKey: string;
  changedNodeIds: string[];
  label: string;
}

// One read-only BPMN diagram. Changed nodes get a marker class (best-effort: a node id that has no
// matching diagram element is simply skipped, so the diagram still renders).
function WorkflowBpmnPane({ xml, changedKey, changedNodeIds, label }: WorkflowBpmnPaneProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container: HTMLDivElement | null = containerRef.current;
    if (!container || !xml) return;

    let cancelled = false;
    const viewer: NavigatedViewer = new NavigatedViewer({
      container,
      moddleExtensions: { custom: customModdleExtension },
      // The custom module is a valid didi module; its inferred literal type doesn't structurally
      // match ModuleDeclaration, so cast through unknown (mirrors how the service registers it).
      additionalModules: [customWFSharedElementsModule() as unknown as ModuleDeclaration]
    });

    viewer
      .importXML(xml)
      .then(() => {
        if (cancelled) return;
        const canvas: BpmnCanvas = viewer.get("canvas") as BpmnCanvas;
        canvas.zoom("fit-viewport");
        for (const nodeId of changedNodeIds) {
          try {
            canvas.addMarker(nodeId, "wf-bpmn-diff__changed");
          } catch {
            // node id without a matching diagram element — ignore
          }
        }
      })
      .catch(() => {
        // invalid/empty xml — the empty-state below covers the no-xml case
      });

    return () => {
      cancelled = true;
      try {
        viewer.destroy();
      } catch {
        // already torn down
      }
    };
    // changedNodeIds is re-derived each render; key it by content to avoid needless re-imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xml, changedKey]);

  return (
    <div className="wf-bpmn-diff__pane">
      <div className="wf-bpmn-diff__pane-label">{label}</div>
      {xml ? (
        <div ref={containerRef} className="wf-bpmn-diff__canvas" />
      ) : (
        <div className="wf-bpmn-diff__empty">No diagram</div>
      )}
    </div>
  );
}

export interface WorkflowBpmnDiffProps {
  beforeXml?: string;
  afterXml?: string;
  changedNodeIds: string[];
  beforeLabel?: string;
  afterLabel?: string;
}

/** Side-by-side read-only BPMN diagrams (before | after) with changed nodes highlighted. */
export function WorkflowBpmnDiff({
  beforeXml,
  afterXml,
  changedNodeIds,
  beforeLabel = "Before",
  afterLabel = "After"
}: WorkflowBpmnDiffProps): React.ReactElement {
  const changedKey: string = changedNodeIds.join(",");
  return (
    <div className="wf-bpmn-diff">
      <WorkflowBpmnPane xml={beforeXml} changedKey={changedKey} changedNodeIds={changedNodeIds} label={beforeLabel} />
      <WorkflowBpmnPane xml={afterXml} changedKey={changedKey} changedNodeIds={changedNodeIds} label={afterLabel} />
    </div>
  );
}
